import { useState } from 'react';
import { useActivatePrompt, useCreatePromptVersion, usePromptList } from '../lib/queries.js';

const SLUGS = ['suggest_on_note', 'summarize', 'rewrite', 'translate', 'autolink', 'rag_chat_system'] as const;

export function PromptsPage() {
  const { data } = usePromptList();
  const create = useCreatePromptVersion();
  const activate = useActivatePrompt();
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  type Prompt = NonNullable<typeof data>['prompts'][number];
  const grouped = (data?.prompts ?? []).reduce<Record<string, Prompt[]>>((acc, p) => {
    if (!acc[p.slug]) acc[p.slug] = [];
    acc[p.slug]!.push(p);
    return acc;
  }, {});

  async function saveDraft() {
    if (!editingSlug) return;
    await create.mutateAsync({ slug: editingSlug, body: draft });
    setEditingSlug(null);
    setDraft('');
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-6">Prompts (versionados)</h1>
      <p className="text-sm text-zinc-400 mb-6">
        Nova versão não substitui — só <code>activate</code> troca a ativa. Cache 60s. Defaults
        hardcoded servem como fallback se nenhuma versão estiver ativa.
      </p>

      <div className="space-y-4">
        {SLUGS.map((slug) => {
          const versions = grouped[slug] ?? [];
          const active = versions.find((v) => v.isActive);
          return (
            <div key={slug} className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="font-mono text-sm">{slug}</div>
                <button
                  onClick={() => {
                    setEditingSlug(slug);
                    setDraft(active?.body ?? '');
                  }}
                  className="text-xs rounded bg-zinc-800 px-3 py-1 hover:bg-zinc-700"
                >
                  nova versão
                </button>
              </div>
              {active ? (
                <details>
                  <summary className="cursor-pointer text-xs text-emerald-400">
                    v{active.version} ativo · {versions.length} versões totais
                  </summary>
                  <pre className="text-xs bg-zinc-950 border border-zinc-800 rounded p-3 mt-2 overflow-x-auto whitespace-pre-wrap">
                    {active.body}
                  </pre>
                </details>
              ) : (
                <div className="text-xs text-zinc-500">usando default hardcoded · sem versão ativa</div>
              )}
              {versions.length > 1 && (
                <div className="mt-2 text-xs text-zinc-500">
                  outras:{' '}
                  {versions
                    .filter((v) => !v.isActive)
                    .map((v) => (
                      <button
                        key={v.id}
                        onClick={() => activate.mutate({ id: v.id })}
                        className="text-cyan-400 hover:underline mr-2"
                      >
                        ativar v{v.version}
                      </button>
                    ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {editingSlug && (
        <div className="fixed inset-0 bg-black/60 grid place-items-center p-6" onClick={() => setEditingSlug(null)}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 max-w-3xl w-full"
          >
            <h2 className="text-lg font-semibold mb-2">Nova versão de {editingSlug}</h2>
            <p className="text-xs text-zinc-500 mb-3">
              Variáveis: <code>{'{{content}}'}</code>, <code>{'{{mode}}'}</code>, <code>{'{{style}}'}</code>, etc.
              Salva como inativa — clique "ativar" depois para promover.
            </p>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="w-full h-64 bg-zinc-950 border border-zinc-800 rounded p-3 font-mono text-sm"
            />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setEditingSlug(null)} className="text-sm text-zinc-400 px-3">cancelar</button>
              <button
                onClick={saveDraft}
                disabled={create.isPending}
                className="text-sm bg-rose-500 text-zinc-950 rounded px-3 py-1.5"
              >
                {create.isPending ? 'salvando…' : 'Criar versão'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
